import type { CustomFacetValue } from 'components/commercetools-ui/organisms/product/product-list-algolia/types'

type ContentstackFacetValueFields = {
  label_de?: string
  value?: string
  label_cz?: string
  value_cz?: string
}

type ContentstackAlgoliaFacetValuesEntry = {
  facet_values?: ({ facet_value?: ContentstackFacetValueFields } & ContentstackFacetValueFields)[]
}

function resolveCustomFacetValue(
  facetValue: ContentstackFacetValueFields | undefined,
  locale: string,
): string | undefined {
  if (!facetValue) return undefined

  const defaultValue = facetValue.value?.trim()
  if (!defaultValue) return undefined

  if (locale === 'cs-CZ') {
    return facetValue.value_cz?.trim() || defaultValue
  }

  return defaultValue
}

function resolveCustomFacetLabel(
  facetValue: ContentstackFacetValueFields,
  locale: string,
  value: string,
): string {
  if (locale === 'cs-CZ') {
    return facetValue.label_cz ?? facetValue.label_de ?? value
  }

  return facetValue.label_de ?? value
}

/**
 * Algolia `categoryPageId` values are locale-prefixed paths (`de-DE > Hund`).
 * CMS often stores the short segment (`Hund`) — prepend locale when missing.
 */
export function withLocaleCategoryPageIdPrefix(value: string, locale: string): string {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  if (trimmed === locale || trimmed.startsWith(`${locale} > `)) return trimmed
  return `${locale} > ${trimmed}`
}

/** Contentstack Delivery API returns references as arrays even for single refs. */
function unwrapCustomFacetValuesReference(
  customFacetValues: ContentstackAlgoliaFacetValuesEntry | ContentstackAlgoliaFacetValuesEntry[] | undefined,
): ContentstackAlgoliaFacetValuesEntry | undefined {
  if (!customFacetValues) return undefined
  return Array.isArray(customFacetValues) ? customFacetValues[0] : customFacetValues
}

/** Supports nested `{ facet_value: {...} }` groups and flat `{ value, label_de }` rows. */
function readFacetValueFields(
  row: { facet_value?: ContentstackFacetValueFields } & ContentstackFacetValueFields,
): ContentstackFacetValueFields | undefined {
  if (row.facet_value) return row.facet_value
  if (row.value || row.value_cz || row.label_de || row.label_cz) return row
  return undefined
}

export function mapContentstackCustomFacetValues(
  customFacetValues: ContentstackAlgoliaFacetValuesEntry | ContentstackAlgoliaFacetValuesEntry[] | undefined,
  locale: string,
): CustomFacetValue[] {
  const entry = unwrapCustomFacetValuesReference(customFacetValues)
  if (!entry?.facet_values?.length) return []

  return entry.facet_values.flatMap((row) => {
    const facetValue = readFacetValueFields(row)
    if (!facetValue) return []

    const value = resolveCustomFacetValue(facetValue, locale)
    if (!value) return []

    return [{ value, label: resolveCustomFacetLabel(facetValue, locale, value) }]
  })
}
